import React, { useState, useMemo } from 'react';
import { CheckCircle, Zap, XCircle, Terminal, Lock, Unlock, AlertTriangle, Shield, Target, Code } from 'lucide-react';

const MAX_ATTEMPTS = 3;

const LEVELS = [
    {
        level: 1,
        mission: "Bypass login without valid credentials",
        sqlTemplate: "SELECT * FROM users WHERE username = '[username]' AND password = '[password]'",
        successPattern: /' OR '1'='1/i,
        hint: "Try making the entire WHERE condition always true.",
        explanation: "The classic ' OR '1'='1 injection forces the WHERE clause to always evaluate as true.",
        difficulty: "Easy",
        points: 100
    },
    {
        level: 2,
        mission: "Bypass password check completely",
        sqlTemplate: "SELECT * FROM users WHERE username = '[username]' AND password = '[password]'",
        successPattern: /admin'\s*--/i,
        hint: "SQL comments (--) can disable the password check.",
        explanation: "The `--` comment truncates the query, preventing the password condition from executing.",
        difficulty: "Medium",
        points: 150
    },
    {
        level: 3,
        mission: "Extract all user data",
        sqlTemplate: "SELECT id, name, email FROM users WHERE username = '[username]'",
        successPattern: /UNION\s+SELECT/i,
        hint: "UNION can combine results from multiple SELECTs.",
        explanation: "UNION SELECT allows attackers to append malicious queries and extract sensitive data.",
        difficulty: "Hard",
        points: 200
    }
];

const SQLInjectionSimulator = ({ steps = [], onStepComplete = () => { } }) => {
    const [levelIndex, setLevelIndex] = useState(0);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [result, setResult] = useState(null);
    const [totalScore, setTotalScore] = useState(0);

    const level = LEVELS[levelIndex];

    const currentStep = useMemo(() => {
        return steps.find(step => {
            if (!step?.content) return false;
            try {
                const parsed = JSON.parse(step.content);
                return parsed.simulatorType === 'sql-injection' && parsed.level === level.level;
            } catch {
                return false;
            }
        });
    }, [steps, level]);

    const renderSqlTemplate = () => {
        const tokens = level.sqlTemplate.split(/(\[username\]|\[password\])/g);
        return (
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 rounded-lg border border-cyan-500/30 font-mono text-sm overflow-x-auto">
                {tokens.map((t, i) =>
                    t === '[username]' || t === '[password]' ? (
                        <span key={i} className="text-yellow-400 font-bold animate-pulse">{t}</span>
                    ) : (
                        <span key={i} className="text-cyan-300">{t}</span>
                    )
                )}
            </div>
        );
    };

    const buildQuery = () => {
        return level.sqlTemplate
            .replace('[username]', username || '[username]')
            .replace('[password]', password || '[password]');
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (result?.status === 'locked') return;

        const success = level.successPattern.test(username);

        if (success) {
            setResult({ status: 'success' });
            setTotalScore(prev => prev + level.points);
            if (currentStep) onStepComplete(currentStep.id, currentStep.points);
            return;
        }

        const nextAttempts = attempts + 1;
        setAttempts(nextAttempts);
        setResult(nextAttempts >= MAX_ATTEMPTS ? { status: 'locked' } : { status: 'fail' });
    };

    const nextLevel = () => {
        setLevelIndex(v => v + 1);
        reset();
    };

    const reset = () => {
        setUsername('');
        setPassword('');
        setAttempts(0);
        setResult(null);
        setShowHint(false);
    };

    const getDifficultyColor = (diff) => {
        if (diff === 'Easy') return 'text-green-400 border-green-500';
        if (diff === 'Medium') return 'text-yellow-400 border-yellow-500';
        return 'text-red-400 border-red-500';
    };

    const getAttemptColor = () => {
        const remaining = MAX_ATTEMPTS - attempts;
        if (remaining === 3) return 'text-green-400';
        if (remaining === 2) return 'text-yellow-400';
        return 'text-red-400';
    };

    return (
        <div className="min-h-[100dvh] bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white p-4 sm:p-6">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-500 to-purple-600 p-3 rounded-lg">
                            <Terminal className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-red-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                                SQL Injection Simulator
                            </h1>
                            <p className="text-gray-400 text-sm">Penetration Testing Training Environment</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <div className="bg-gray-800 border border-purple-500/30 px-6 py-3 rounded-lg">
                            <div className="text-xs text-gray-400 mb-1">Total Score</div>
                            <div className="text-2xl font-bold text-purple-400">{totalScore}</div>
                        </div>
                        <div className="bg-gray-800 border border-cyan-500/30 px-6 py-3 rounded-lg">
                            <div className="text-xs text-gray-400 mb-1">Level Progress</div>
                            <div className="text-2xl font-bold text-cyan-400">{levelIndex + 1}/{LEVELS.length}</div>
                        </div>
                    </div>
                </div>

                {/* Level Indicator */}
                <div className="flex gap-2 mb-6">
                    {LEVELS.map((l, idx) => (
                        <div
                            key={idx}
                            className={`flex-1 h-2 rounded-full transition-all ${idx < levelIndex ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                    idx === levelIndex ? 'bg-gradient-to-r from-yellow-500 to-orange-500 animate-pulse' :
                                        'bg-gray-700'
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto grid grid-cols-2 gap-6">
                {/* Left Panel - Mission Briefing */}
                <div className="space-y-4">
                    {/* Mission Card */}
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-purple-500/30 rounded-xl p-6 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl"></div>

                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Target className="w-6 h-6 text-purple-400" />
                                <h2 className="text-2xl font-bold">Mission Briefing</h2>
                            </div>
                            <div className={`px-3 py-1 border-2 rounded-lg text-xs font-semibold ${getDifficultyColor(level.difficulty)}`}>
                                {level.difficulty}
                            </div>
                        </div>

                        <div className="bg-black/40 border border-purple-500/20 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <Shield className="w-5 h-5 text-purple-400 mt-1" />
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">OBJECTIVE</div>
                                    <div className="text-lg font-semibold text-white">{level.mission}</div>
                                    <div className="text-sm text-purple-300 mt-2">Reward: {level.points} points</div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="text-xs text-gray-400 mb-2 flex items-center gap-2">
                                <Code className="w-4 h-4" />
                                SQL QUERY TEMPLATE
                            </div>
                            {renderSqlTemplate()}
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className={`w-5 h-5 ${getAttemptColor()}`} />
                                <span className="text-sm">Attempts Remaining</span>
                            </div>
                            <div className={`text-2xl font-bold ${getAttemptColor()}`}>
                                {MAX_ATTEMPTS - attempts}
                            </div>
                        </div>
                    </div>

                    {/* Hint Card */}
                    <div className={`bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-2 rounded-xl p-5 transition-all ${showHint ? 'border-yellow-500/50' : 'border-gray-700'
                        }`}>
                        <button
                            onClick={() => setShowHint(v => !v)}
                            className="w-full flex items-center justify-between text-left group"
                        >
                            <div className="flex items-center gap-2">
                                <Zap className="w-5 h-5 text-yellow-400" />
                                <span className="font-semibold">Need a Hint?</span>
                            </div>
                            <span className="text-sm text-gray-400 group-hover:text-white transition-colors">
                                {showHint ? 'Hide' : 'Show'}
                            </span>
                        </button>
                        {showHint && (
                            <div className="mt-4 p-4 bg-black/40 rounded-lg border border-yellow-500/30">
                                <p className="text-yellow-200">{level.hint}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Login Interface */}
                <div className="space-y-4">
                    {/* Login Card */}
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-cyan-500/30 rounded-xl p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl"></div>

                        <div className="flex items-center gap-3 mb-6">
                            {result?.status === 'success' ? (
                                <Unlock className="w-6 h-6 text-green-400" />
                            ) : (
                                <Lock className="w-6 h-6 text-cyan-400" />
                            )}
                            <h2 className="text-2xl font-bold">Target Login Portal</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    className="w-full bg-gray-900 border-2 border-gray-700 rounded-lg px-4 py-3 text-white font-mono focus:border-cyan-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={result?.status === 'locked'}
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    placeholder="Enter username..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    className="w-full bg-gray-900 border-2 border-gray-700 rounded-lg px-4 py-3 text-white font-mono focus:border-cyan-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={result?.status === 'locked'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter password..."
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={result?.status === 'locked'}
                                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Execute Attack
                                </button>
                                <button
                                    onClick={reset}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>

                        {/* Result Display */}
                        {result && (
                            <div className={`mt-6 p-4 rounded-lg border-2 ${result.status === 'success' ? 'bg-green-500/10 border-green-500' :
                                    result.status === 'locked' ? 'bg-red-500/10 border-red-500' :
                                        'bg-yellow-500/10 border-yellow-500'
                                }`}>
                                <div className="flex items-center gap-2 mb-3">
                                    {result.status === 'success' && (
                                        <>
                                            <CheckCircle className="w-5 h-5 text-green-400" />
                                            <span className="font-bold text-green-400">ACCESS GRANTED!</span>
                                        </>
                                    )}
                                    {result.status === 'fail' && (
                                        <>
                                            <XCircle className="w-5 h-5 text-yellow-400" />
                                            <span className="font-bold text-yellow-400">ACCESS DENIED</span>
                                        </>
                                    )}
                                    {result.status === 'locked' && (
                                        <>
                                            <XCircle className="w-5 h-5 text-red-400" />
                                            <span className="font-bold text-red-400">SYSTEM LOCKED</span>
                                        </>
                                    )}
                                </div>

                                <div className="bg-black/60 rounded-lg p-3 border border-gray-700">
                                    <div className="text-xs text-gray-400 mb-1">EXECUTED QUERY:</div>
                                    <pre className="text-cyan-300 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                                        {buildQuery()}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Explanation Card */}
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Terminal className="w-5 h-5 text-purple-400" />
                            <h3 className="text-xl font-bold">Technical Analysis</h3>
                        </div>

                        {(result?.status === 'success' || result?.status === 'locked') ? (
                            <>
                                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                                    <p className="text-gray-300 leading-relaxed">{level.explanation}</p>
                                </div>

                                {levelIndex < LEVELS.length - 1 ? (
                                    <button
                                        onClick={nextLevel}
                                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                                    >
                                        Next Level
                                        <span className="text-xl">→</span>
                                    </button>
                                ) : (
                                    <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500 rounded-lg p-5 text-center">
                                        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                                        <h3 className="text-xl font-bold text-green-400 mb-2">Mission Complete!</h3>
                                        <p className="text-gray-300 mb-3">
                                            You've mastered SQL injection techniques.
                                        </p>
                                        <div className="bg-black/40 border border-green-500/30 rounded p-3">
                                            <strong className="text-green-400">Security Lesson:</strong>
                                            <p className="text-sm text-gray-300 mt-1">
                                                Always use parameterized queries and input validation to prevent SQL injection attacks.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Lock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Complete the challenge to unlock the technical analysis</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SQLInjectionSimulator;